import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Minus, Plus, ShoppingCart, MessageCircle, ChevronRight, Truck, ShieldCheck, RefreshCw, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { productsAPI } from '@/utils/api';
import { formatCOP, buildWhatsAppUrl } from '@/utils/format';
import { resolveImageUrl } from '@/utils/images';
import { useCart } from '@/context/CartContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import ProductCard from '@/components/product/ProductCard';
import ProductReviews from '@/components/product/ProductReviews';

const ProductDetail = () => {
  const { slug } = useParams();
  const { addItem, setIsOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.dataset.stickyBar = '1';
    return () => { delete document.body.dataset.stickyBar; };
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const p = await productsAPI.getById(slug);
        setProduct(p);
        setSelectedVariant(p.variants?.[0]);

        const rel = await productsAPI.getAll({
          species: p.species,
          brand: p.brand_slug,
          limit: 4
        });
        setRelated(rel.filter((r) => r.id !== p.id).slice(0, 4));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading || !product) {
    return (
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="aspect-square bg-[#F7F2E8] rounded-3xl animate-pulse" />
          <div className="space-y-4">
            <div className="h-6 bg-[#F7F2E8] rounded animate-pulse w-1/4" />
            <div className="h-10 bg-[#F7F2E8] rounded animate-pulse" />
            <div className="h-6 bg-[#F7F2E8] rounded animate-pulse w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    if (selectedVariant) {
      addItem(product, selectedVariant, quantity);
    }
  };

  const handleBuyNow = () => {
    handleAddToCart();
    setIsOpen(true);
  };

  const whatsAppMessage = `Hola, estoy viendo el producto ${product.name} en la presentación ${selectedVariant?.size} y quisiera recibir asesoría`;

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12 pb-28 lg:pb-12">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1 text-xs text-[#57534E] mb-8" data-testid="product-breadcrumbs">
        <Link to="/" className="hover:text-[#2A5C43]">Inicio</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to={`/${product.species === 'perro' ? 'perros' : 'gatos'}`} className="hover:text-[#2A5C43] capitalize">
          {product.species === 'perro' ? 'Perros' : 'Gatos'}
        </Link>
        <ChevronRight className="w-3 h-3" />
        <Link to={`/marcas/${product.brand_slug}`} className="hover:text-[#2A5C43]">{product.brand}</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-[#1C1917] font-medium truncate">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-12 mb-16">
        {/* Gallery */}
        <div>
          <div className={`${product.images?.[0] && !imgError ? 'aspect-square' : 'aspect-[16/9] sm:aspect-square'} bg-[#F7F2E8] rounded-3xl overflow-hidden mb-4`}>
            {product.images?.[0] && !imgError ? (
              <img src={resolveImageUrl(product.images[0], 1000)} alt={product.name} onError={() => setImgError(true)} className="w-full h-full object-cover" loading="lazy" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-center p-6" data-testid="product-noimage">
                <span className="font-display font-bold text-xl sm:text-3xl text-[#2A5C43] uppercase tracking-wide">{product.brand}</span>
                <span className="text-xs sm:text-sm text-[#8BA58F] mt-1">{product.name}</span>
                <span className="text-[10px] uppercase tracking-widest text-[#8BA58F]/70 mt-3">Imagen próximamente</span>
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">{product.brand}</p>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight leading-tight mb-4">
            {product.name}
          </h1>
          <div className="mb-4">
            <ProductReviews productId={product.id} compact />
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {product.is_grain_free && (
              <span className="text-[10px] tracking-wider uppercase font-semibold px-2.5 py-1 rounded-full bg-[#8BA58F]/20 text-[#2A5C43]">Libre de granos</span>
            )}
            {product.is_holistic && (
              <span className="text-[10px] tracking-wider uppercase font-semibold px-2.5 py-1 rounded-full bg-[#2A5C43]/10 text-[#2A5C43]">Holístico</span>
            )}
            {product.is_specialized && (
              <span className="text-[10px] tracking-wider uppercase font-semibold px-2.5 py-1 rounded-full bg-[#D47A4A]/10 text-[#D47A4A]">Nutrición especializada</span>
            )}
          </div>

          <p className="text-[#57534E] leading-relaxed mb-6">{product.short_description || product.description}</p>

          {/* Specialized nutrition warning */}
          {product.is_specialized && (
            <div className="bg-[#F5E6D3] border border-[#D47A4A]/30 rounded-xl p-4 mb-6" data-testid="specialized-warning">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-[#D47A4A] shrink-0 mt-0.5" />
                <p className="text-sm text-[#6B5D3F]">
                  <strong>Producto de nutrición especializada.</strong> Antes de cambiar la alimentación de tu mascota, consulta con un médico veterinario.
                </p>
              </div>
            </div>
          )}

          {/* Price */}
          <div className="mb-6">
            <p className="font-display font-bold text-4xl text-[#2A5C43]" data-testid="product-price">
              {formatCOP(selectedVariant?.price_retail_with_tax || 0)}
            </p>
            <p className="text-xs text-[#57534E] mt-1">IVA incluido • Pago seguro con Bold</p>
          </div>

          {/* Variant selector */}
          {product.variants?.length > 1 && (
            <div className="mb-6">
              <p className="text-sm font-semibold text-[#1C1917] mb-3">Presentación</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {product.variants.map((v) => (
                  <button
                    key={v.variant_id}
                    onClick={() => setSelectedVariant(v)}
                    disabled={v.stock === 0}
                    data-testid={`variant-${v.variant_id}`}
                    className={`px-3 py-3 rounded-xl border-2 text-left transition-all ${
                      selectedVariant?.variant_id === v.variant_id
                        ? 'border-[#2A5C43] bg-[#2A5C43]/5'
                        : 'border-[#E7E5E4] hover:border-[#8BA58F]'
                    } ${v.stock === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    <p className="text-sm font-semibold text-[#1C1917]">{v.size}</p>
                    <p className="text-xs text-[#57534E]">{v.weight_original}</p>
                    <p className="text-xs font-semibold text-[#2A5C43] mt-1">{formatCOP(v.price_retail_with_tax)}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Stock */}
          <div className="mb-6">
            {selectedVariant?.stock > 0 ? (
              <p className="text-sm text-[#2A5C43] font-medium">✓ Disponible • {selectedVariant.stock} unidades</p>
            ) : (
              <p className="text-sm text-red-600 font-medium">Agotado</p>
            )}
          </div>

          {/* Quantity */}
          <div className="flex items-center gap-4 mb-6">
            <p className="text-sm font-semibold text-[#1C1917]">Cantidad</p>
            <div className="flex items-center gap-2 border border-[#E7E5E4] rounded-full bg-white">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#F7F2E8]"
                data-testid="qty-decrease"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-base font-semibold w-8 text-center" data-testid="qty-value">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#F7F2E8]"
                data-testid="qty-increase"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <Button
              onClick={handleAddToCart}
              disabled={!selectedVariant || selectedVariant.stock === 0}
              className="flex-1 bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full py-6 font-semibold"
              data-testid="product-add-to-cart"
            >
              <ShoppingCart className="w-4 h-4 mr-2" /> Agregar al carrito
            </Button>
            <Button
              onClick={handleBuyNow}
              disabled={!selectedVariant || selectedVariant.stock === 0}
              className="flex-1 bg-[#D47A4A] hover:bg-[#B85F35] text-white rounded-full py-6 font-semibold"
              data-testid="product-buy-now"
            >
              Comprar ahora
            </Button>
          </div>

          <a
            href={buildWhatsAppUrl(whatsAppMessage)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 py-3 px-4 rounded-full bg-white border border-[#25D366] text-[#25D366] hover:bg-[#25D366]/5 text-sm font-semibold mb-8"
            data-testid="product-whatsapp"
          >
            <MessageCircle className="w-4 h-4" /> Consultar por WhatsApp
          </a>

          {/* Trust indicators */}
          <div className="grid grid-cols-3 gap-3 text-center border-t border-[#E7E5E4] pt-6">
            <div>
              <Truck className="w-5 h-5 text-[#2A5C43] mx-auto mb-1" />
              <p className="text-[11px] text-[#57534E]">Envío a toda Colombia</p>
            </div>
            <div>
              <ShieldCheck className="w-5 h-5 text-[#2A5C43] mx-auto mb-1" />
              <p className="text-[11px] text-[#57534E]">Pago seguro Bold</p>
            </div>
            <div>
              <RefreshCw className="w-5 h-5 text-[#2A5C43] mx-auto mb-1" />
              <p className="text-[11px] text-[#57534E]">Cambios y devoluciones</p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed info */}
      <div className="grid lg:grid-cols-3 gap-8 mb-16">
        <div className="lg:col-span-2">
          <Accordion type="single" collapsible defaultValue="description" className="space-y-3">
            <AccordionItem value="description" className="bg-white rounded-2xl border border-[#E7E5E4] px-6">
              <AccordionTrigger className="font-display font-semibold text-[#1C1917]" data-testid="acc-description">
                Descripción
              </AccordionTrigger>
              <AccordionContent className="text-[#57534E] leading-relaxed">
                {product.description}
              </AccordionContent>
            </AccordionItem>

            {product.benefits?.length > 0 && (
              <AccordionItem value="benefits" className="bg-white rounded-2xl border border-[#E7E5E4] px-6">
                <AccordionTrigger className="font-display font-semibold text-[#1C1917]" data-testid="acc-benefits">
                  Beneficios principales
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2">
                    {product.benefits.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-[#57534E]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#2A5C43] mt-2 shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            )}

            {product.ingredients && (
              <AccordionItem value="ingredients" className="bg-white rounded-2xl border border-[#E7E5E4] px-6">
                <AccordionTrigger className="font-display font-semibold text-[#1C1917]" data-testid="acc-ingredients">
                  Ingredientes
                </AccordionTrigger>
                <AccordionContent className="text-[#57534E] leading-relaxed">
                  {product.ingredients}
                </AccordionContent>
              </AccordionItem>
            )}

            <AccordionItem value="shipping" className="bg-white rounded-2xl border border-[#E7E5E4] px-6">
              <AccordionTrigger className="font-display font-semibold text-[#1C1917]" data-testid="acc-shipping">
                Información de envío
              </AccordionTrigger>
              <AccordionContent className="text-[#57534E] leading-relaxed">
                Enviamos a todo Colombia. Tiempo estimado: 3-5 días hábiles en ciudades principales. El costo se calcula según peso y destino al finalizar tu compra.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <aside className="space-y-4">
          <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6">
            <p className="font-display font-semibold text-[#1C1917] mb-3">Detalles del producto</p>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-[#57534E]">Marca</dt>
                <dd className="text-[#1C1917] font-medium">{product.brand}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-[#57534E]">Especie</dt>
                <dd className="text-[#1C1917] font-medium capitalize">{product.species}</dd>
              </div>
              {product.protein && (
                <div className="flex justify-between">
                  <dt className="text-[#57534E]">Proteína</dt>
                  <dd className="text-[#1C1917] font-medium">{product.protein}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-[#57534E]">Etapa</dt>
                <dd className="text-[#1C1917] font-medium capitalize">{product.life_stage?.join(', ')}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-[#57534E]">Formato</dt>
                <dd className="text-[#1C1917] font-medium capitalize">{product.format}</dd>
              </div>
              {selectedVariant && (
                <>
                  <div className="flex justify-between">
                    <dt className="text-[#57534E]">Referencia</dt>
                    <dd className="text-[#1C1917] font-medium text-xs">{selectedVariant.sku}</dd>
                  </div>
                  {selectedVariant.barcode && (
                    <div className="flex justify-between">
                      <dt className="text-[#57534E]">Cód. barras</dt>
                      <dd className="text-[#1C1917] font-medium text-xs">{selectedVariant.barcode}</dd>
                    </div>
                  )}
                </>
              )}
            </dl>
          </div>
        </aside>
      </div>

      {/* Reviews */}
      <div className="mt-16 mb-12">
        <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Opiniones</p>
        <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#1C1917] mb-6">Lo que dicen nuestros clientes</h2>
        <ProductReviews productId={product.id} />
      </div>

      {/* Related products */}
      {related.length > 0 && (
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">También te puede gustar</p>
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#1C1917] mb-8">Productos relacionados</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      )}

      {/* Mobile sticky buy bar */}
      {selectedVariant && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[#E7E5E4] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]" data-testid="mobile-buy-bar">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-wider text-[#8BA58F] truncate">{selectedVariant.size} · IVA incluido</p>
              <p className="font-display font-bold text-lg text-[#2A5C43] leading-tight">{formatCOP(selectedVariant.price_retail_with_tax * quantity)}</p>
            </div>
            <Button
              onClick={handleAddToCart}
              disabled={selectedVariant.stock === 0}
              className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-5 h-11 font-semibold"
              data-testid="mobile-add-to-cart"
            >
              <ShoppingCart className="w-4 h-4 mr-2" /> Agregar
            </Button>
            <Button
              onClick={handleBuyNow}
              disabled={selectedVariant.stock === 0}
              className="bg-[#D47A4A] hover:bg-[#B85F35] text-white rounded-full px-4 h-11 font-semibold"
              data-testid="mobile-buy-now"
            >
              Comprar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
